import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { submitContact } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Seo from "@/components/Seo";

const INTERESTS = [
  "Photography",
  "Video Clips",
  "Drone",
  "Project Film",
  "Event Coverage",
];

const initialState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  interested_in: [],
  website: "", // honeypot - must stay empty
};

export default function ContactPage() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const project = params.get("project");
    if (project) {
      setForm((f) =>
        f.message ? f : { ...f, message: `Hello, I'd like coverage similar to "${project}". ` }
      );
    }
  }, [params]);

  const toggleInterest = (label) => {
    setForm((f) => ({
      ...f,
      interested_in: f.interested_in.includes(label)
        ? f.interested_in.filter((x) => x !== label)
        : [...f.interested_in, label],
    }));
  };

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      setForm(initialState);
      toast.success("Brief received, we'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not send, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-10 pt-10 lg:pt-12 pb-20">
      <Seo
        title="Contact, Plan a Shoot"
        description="Tell us about the scheme, timeline and the coverage you need. We'll come back within one working day."
        path="/contact"
      />
      <PageHeader
        eyebrow="Plan a Shoot"
        title="Let's talk."
        intro="Tell us about the scheme, timeline and the coverage you need. We'll come back within one working day."
        testid="contact-header"
      />

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 flex flex-col gap-1"
        >
          {/* Honeypot - visually hidden, real users won't fill it */}
          <input
            type="text"
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <input
              data-testid="contact-name"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Name"
              className="hl-input"
            />
            <input
              data-testid="contact-company"
              name="company"
              value={form.company}
              onChange={onChange}
              placeholder="Company"
              className="hl-input"
            />
            <input
              data-testid="contact-email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="Email"
              className="hl-input"
            />
            <input
              data-testid="contact-phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="Phone"
              className="hl-input"
            />
          </div>

          <textarea
            data-testid="contact-message"
            name="message"
            value={form.message}
            onChange={onChange}
            placeholder="Tell us about the project - site, dates, deliverables…"
            className="hl-input mt-2"
            rows={5}
          />

          <div className="mt-8">
            <h2 className="text-[10px] tracking-[0.32em] uppercase text-[#737373] mb-4">
              Interested in
            </h2>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((label) => {
                const active = form.interested_in.includes(label);
                return (
                  <button
                    type="button"
                    key={label}
                    onClick={() => toggleInterest(label)}
                    data-testid={`interest-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`hl-chip flex items-center gap-2 ${active ? "active" : ""}`}
                  >
                    {active && <Check size={12} />}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="hl-btn-primary mt-10 self-start disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Plan a Shoot"}
          </button>

          {done && (
            <div
              className="text-[12px] tracking-[0.24em] uppercase text-[#658CAE] mt-5"
              data-testid="contact-success"
            >
              Brief received, thank you.
            </div>
          )}
        </form>

        <aside className="lg:col-span-5 lg:pl-10 lg:border-l border-[#1a1a1a]">
          <h2 className="text-[10px] tracking-[0.32em] uppercase text-[#737373] mb-4">
            Studio
          </h2>
          <h3 className="font-display text-2xl tracking-tight text-white mb-2">
            HighwaysLens
          </h3>
          <p className="text-[14px] text-[#A3A3A3] leading-relaxed">
            UK-based. Coverage across England, Scotland and Wales.
            Night works, possession windows and short-notice commissions welcome.
          </p>

          <div className="mt-8 space-y-4 text-[13px] text-[#c9d1d9]">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-1">
                Email
              </div>
              studio@highwayslens.co.uk
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-1">
                Hours
              </div>
              Mon - Sun · 24 hours for live works
            </div>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373] mb-1">
                Insurance
              </div>
              Public liability · CSCS · CAA drone permitted
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
